/**
 * Utilitários para detecção de dispositivo e plataforma
 * Detecta automaticamente o tipo de dispositivo e sistema operacional do usuário
 */

import { DeviceType, Platform } from '@/lib/utils/deviceTypes'

/**
 * Detecta o tipo de dispositivo baseado no User Agent
 */
export function detectDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'desktop'
  
  const userAgent = navigator.userAgent.toLowerCase()
  
  // Detectar tablet primeiro (antes de mobile)
  if (/(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)) {
    return 'tablet'
  }
  
  // Detectar mobile
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
    return 'mobile'
  }
  
  // Default: desktop
  return 'desktop'
}

/**
 * Detecta a plataforma/sistema operacional
 */
export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'other'
  
  const userAgent = navigator.userAgent.toLowerCase()
  const platform = navigator.platform?.toLowerCase() || ''
  
  // Windows
  if (/win/i.test(platform) || /windows/i.test(userAgent)) {
    return 'windows'
  }
  
  // macOS
  if (/mac/i.test(platform) && !/iphone|ipad|ipod/.test(userAgent)) {
    return 'macos'
  }
  
  // iOS
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios'
  }
  
  // Android
  if (/android/.test(userAgent)) {
    return 'android'
  }
  
  // Linux
  if (/linux/.test(platform) || /linux/.test(userAgent)) {
    return 'linux'
  }
  
  // Other
  return 'other'
}

/**
 * Obtém informações completas do dispositivo
 */
export function getDeviceInfo() {
  return {
    device_type: detectDeviceType(),
    platform: detectPlatform(),
    last_access: new Date().toISOString(),
  }
}

/**
 * Formata número de telefone brasileiro
 * Remove caracteres não numéricos e formata como (XX) XXXXX-XXXX
 */
export function formatPhone(phone: string): string {
  const numbersOnly = phone.replace(/\D/g, '')

  if (numbersOnly.length === 11) {
    return numbersOnly.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }

  if (numbersOnly.length === 10) {
    return numbersOnly.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  }

  return phone
}

/**
 * Valida número de telefone brasileiro
 */
export function validatePhone(phone: string): boolean {
  const numbersOnly = phone.replace(/\D/g, '')
  return numbersOnly.length === 10 || numbersOnly.length === 11
}

/**
 * Formata username de rede social (remove @ se houver)
 */
export function formatUsername(username: string): string {
  return username.trim().replace(/^@/, '')
}
